import React from 'react';
import ForecastChart from '../Charts/ForecastChart';
import CommodityForecastChart from '../Charts/CommodityForecastChart';
import MonthlyForecastChart from '../Charts/MonthlyForecastChart';
import CapacityForecastChart from '../Charts/CapacityForecastChart';
import { DrilldownDataService } from '../../services/DrilldownDataService';

const ForecastingTab = ({ showDetails, openDrillDown }) => {
  const forecasts = [
    { id: 'q1-2024', quarter: 'Q1 2024', forecast: '2.05M T', confidence: 'High', growth: '+5.2%' },
    { id: 'q2-2024', quarter: 'Q2 2024', forecast: '2.18M T', confidence: 'High', growth: '+6.3%' },
    { id: 'q3-2024', quarter: 'Q3 2024', forecast: '2.25M T', confidence: 'Medium', growth: '+3.8%' },
    { id: 'q4-2024', quarter: 'Q4 2024', forecast: '2.45M T', confidence: 'Medium', growth: '+8.9%' }
  ];

  const getConfidenceClass = (confidence) => {
    switch(confidence.toLowerCase()) {
      case 'high': return 'status-indicator status-high';
      case 'medium': return 'status-indicator status-medium';
      case 'low': return 'status-indicator status-low';
      default: return 'status-indicator';
    }
  };

  // Handle forecast row click
  const handleForecastClick = (forecast) => {
    if (openDrillDown) {
      const detailedData = DrilldownDataService.getForecastDetails(forecast.id);
      openDrillDown('forecast', detailedData, `${forecast.quarter} Forecast Analysis`);
    }
  };

  // Handle metric card click
  const handleMetricClick = (metric) => {
  if (openDrillDown) {
    const metrics = {
      'accuracy-rate': {
        type: 'insight',
        title: 'Forecasting Accuracy Analysis',
        summary: 'Current forecasting accuracy is at 94.2%, showing significant improvement from last year.',
        icon: 'fas fa-percentage',
        iconColor: '#4da6ff',
        category: 'Performance Analysis',
        confidence: 'High Confidence',
        keyMetrics: [
          { label: 'Current Accuracy', value: '94.2%', change: '+2.1% from last year' },
          { label: 'Monthly Accuracy', value: '92.5%', change: 'Stable' },
          { label: 'Quarterly Accuracy', value: '94.2%', change: 'Improved' },
          { label: 'Annual Accuracy', value: '96.8%', change: 'Excellent' }
        ],
        contributingFactors: [
          'Improved data collection methodologies',
          'Advanced algorithm implementation',
          'Seasonal pattern adjustments',
          'Enhanced market intelligence integration'
        ],
        details: 'Accuracy has improved due to several key factors:',
        impact: 'Improved accuracy leads to better capacity planning and resource allocation.',
        nextSteps: [
          'Implement machine learning models',
          'Expand data sources for better prediction',
          'Conduct quarterly model validation'
        ]
      },
      'error-margin': {
        type: 'insight',
        title: 'Error Margin Analysis',
        summary: 'Error margin currently at ±3.5%, showing improvement from previous period.',
        icon: 'fas fa-chart-bar',
        iconColor: '#4cd964',
        category: 'Performance Analysis',
        confidence: 'Medium Confidence',
        keyMetrics: [
          { label: 'Current Margin', value: '±3.5%', change: '-0.8% improvement' },
          { label: 'Data Quality Impact', value: '±1.2%', change: 'Good' },
          { label: 'Model Limitations', value: '±1.5%', change: 'Acceptable' },
          { label: 'Market Volatility', value: '±0.8%', change: 'Low' }
        ],
        primaryReasons: [
          'Data quality improvements reduced margin by 0.3%',
          'Model enhancements contributed to 0.2% reduction',
          'Market monitoring decreased volatility impact'
        ],
        details: 'Error margin components breakdown:',
        mitigationStrategies: [
          'Improve data collection quality',
          'Validate models with external data',
          'Monitor key market indicators'
        ]
      }
    };
    
    openDrillDown('insight', metrics[metric], metrics[metric].title);
  }
};

  return (
    <div id="forecasting-tab" className="tab-content-wrapper">
      <div className="insights-container">
        <div 
  className="insight-card clickable"
  onClick={() => openDrillDown && openDrillDown('insight', {
    type: 'insight',
    title: 'Growth Forecast Analysis',
    summary: 'Based on current trends, port is projected to handle 2.45M tons in 2024.',
    icon: 'fas fa-crystal-ball',
    iconColor: '#4da6ff',
    category: 'Forecast Analysis',
    confidence: 'High Confidence',
    keyMetrics: [
      { label: '2024 Projection', value: '2.45M T', change: '+12.6%' },
      { label: '2023 Actual', value: '2.18M T', change: 'Baseline' },
      { label: 'Growth Rate', value: '12.6%', change: 'Annual' },
      { label: 'Market Share', value: '24.5%', change: '+1.2%' }
    ],
    contributingFactors: [
      'E-commerce expansion: +25% expected growth',
      'Manufacturing recovery: +8% industrial output',
      'Trade agreements: New international partnerships',
      'Infrastructure investments: Capacity expansion projects'
    ],
    details: 'Key growth drivers and their impact:',
    risks: [
      'Economic slowdown in key markets',
      'Geopolitical tensions affecting trade',
      'Supply chain disruptions'
    ],
    nextSteps: [
      'Monitor economic indicators',
      'Prepare for peak season capacity',
      'Develop contingency plans'
    ],
    timeline: 'Q3 2024 review of projections'
  }, 'Growth Forecast Analysis')}
>
          <div className="insight-title"><i className="fas fa-crystal-ball"></i> Growth Forecast</div>
          <div className="insight-content">Based on current trends, port is projected to handle 2.45M tons in 2024, a 12.6% increase from 2023.</div>
        </div>
        
        <div 
          className="insight-card success clickable"
          onClick={() => openDrillDown && openDrillDown('container-forecast', {
            title: 'Containerized Growth Forecast',
            content: 'Containerized cargo expected to grow 14.2% in 2024, reaching 1.14M tons.',
            details: 'Primary drivers:',
            drivers: [
              'E-commerce: +30% volume',
              'Consumer goods: +12%',
              'Electronics: +15%',
              'Pharmaceuticals: +18%'
            ],
            impact: 'Will require additional 50,000 TEU capacity'
          }, 'Container Forecast')}
        >
          <div className="insight-title"><i className="fas fa-chart-line"></i> Containerized Growth</div>
          <div className="insight-content">Containerized cargo expected to grow 14.2% in 2024, reaching 1.14M tons, driven by e-commerce expansion.</div>
        </div>
        
        <div 
          className="insight-card warning clickable"
          onClick={() => openDrillDown && openDrillDown('capacity-forecast', {
            title: 'Capacity Constraint Analysis',
            content: 'At current growth rate, port will reach 95% capacity by Q3 2024.',
            details: 'Capacity analysis:',
            current: {
              'Design Capacity': '3.5M TEU',
              'Current Utilization': '82%',
              'Projected Q3 2024': '95%',
              'Time to full capacity': '8 months'
            },
            recommendations: [
              'Activate Berth 8 immediately',
              'Expand container yard',
              'Add 5 new cranes',
              'Optimize operations'
            ]
          }, 'Capacity Forecast')}
        >
          <div className="insight-title"><i className="fas fa-exclamation-triangle"></i> Capacity Constraints</div>
          <div className="insight-content">At current growth rate, port will reach 95% capacity by Q3 2024, requiring expansion planning.</div>
        </div>
      </div>

      <div className="ports-dashboard">
        <h3 className="section-title">
          <i className="fas fa-bullseye"></i>
          <span>Forecasting Accuracy Metrics</span>
        </h3>
      </div>
      
      <div className="dashboard-cards">
        <div 
          className="card clickable"
          onClick={() => handleMetricClick('accuracy-rate')}
        >
          <div className="card-header">
            <div className="card-title">Accuracy Rate</div>
            <div className="card-icon" style={{backgroundColor: '#4da6ff'}}>
              <i className="fas fa-percentage"></i>
            </div>
          </div>
          <div className="card-content">
            <div className="card-value">94.2%</div>
            <div className="card-change positive">
              <i className="fas fa-arrow-up"></i>
              <span>+2.1% from last year</span>
            </div>
          </div>
        </div>
        
        <div 
          className="card clickable"
          onClick={() => handleMetricClick('error-margin')}
        >
          <div className="card-header">
            <div className="card-title">Error Margin</div>
            <div className="card-icon" style={{backgroundColor: '#4cd964'}}>
              <i className="fas fa-chart-bar"></i>
            </div>
          </div>
          <div className="card-content">
            <div className="card-value">±3.5%</div>
            <div className="card-change positive">
              <i className="fas fa-arrow-down"></i>
              <span>-0.8% improvement</span>
            </div>
          </div>
        </div>
        
        <div 
          className="card clickable"
          onClick={() => openDrillDown && openDrillDown('model-confidence', {
            title: 'Model Confidence Analysis',
            value: '88.7%',
            icon: 'fas fa-brain',
            color: '#ff9f40',
            details: 'Confidence level based on:',
            factors: [
              'Data quality: 92%',
              'Model fit: 87%',
              'Market stability: 85%',
              'Historical accuracy: 91%'
            ],
            recommendations: ['Improve data collection', 'Validate with external data', 'Monitor key indicators']
          }, 'Model Confidence')}
        >
          <div className="card-header">
            <div className="card-title">Model Confidence</div>
            <div className="card-icon" style={{backgroundColor: '#ff9f40'}}>
              <i className="fas fa-brain"></i>
            </div>
          </div>
          <div className="card-content">
            <div className="card-value">88.7%</div>
            <div className="progress-bar">
              <div className="progress-fill" style={{width: '88.7%', backgroundColor: '#ff9f40'}}></div>
            </div>
          </div>
        </div>
        
        <div 
          className="card clickable"
          onClick={() => openDrillDown && openDrillDown('seasonal-factor', {
            title: 'Seasonal Factor Analysis',
            value: '1.15x',
            icon: 'fas fa-cloud-sun',
            color: '#9966ff',
            details: 'Peak season analysis:',
            months: {
              'Highest': 'October (1.25x)',
              'Lowest': 'February (0.85x)',
              'Average': '1.15x',
              'Peak period': 'September-November'
            },
            planning: 'Increase capacity by 15% during peak season'
          }, 'Seasonal Analysis')}
        >
          <div className="card-header">
            <div className="card-title">Seasonal Factor</div>
            <div className="card-icon" style={{backgroundColor: '#9966ff'}}>
              <i className="fas fa-cloud-sun"></i>
            </div>
          </div>
          <div className="card-content">
            <div className="card-value">1.15x</div>
            <div className="card-change positive">
              <i className="fas fa-arrow-up"></i>
              <span>Peak season multiplier</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="charts-section">
        <div className="chart-container">
          <h3 className="section-title">
            <i className="fas fa-crystal-ball"></i>
            <span>Annual Throughput Forecast</span>
          </h3>
          <div style={{height: '400px'}}>
            <ForecastChart 
              onClick={() => openDrillDown && openDrillDown('annual-forecast', {
                title: 'Annual Forecast Analysis',
                years: '2020-2025',
                growthRate: 'Average 7.8% annually',
                highlights: [
                  '2024 expected: 2.45M tons',
                  '2025 projected: 2.65M tons',
                  '5-year CAGR: 8.2%'
                ],
                assumptions: ['Economic growth 3.5%', 'Trade growth 4.2%', 'No major disruptions']
              }, 'Annual Forecast')}
            />
          </div>
        </div>
        
        <div className="chart-container">
          <h3 className="section-title">
            <i className="fas fa-boxes"></i>
            <span>Commodity Growth Forecast 2024</span>
          </h3>
          <div style={{height: '400px'}}>
            <CommodityForecastChart 
              onClick={() => openDrillDown && openDrillDown('commodity-forecast', {
                title: 'Commodity Forecast 2024',
                details: 'Expected growth by commodity:',
                commodities: [
                  { name: 'Containerized', growth: '+14.2%', drivers: 'E-commerce, manufacturing' },
                  { name: 'Dry Bulk', growth: '+9.5%', drivers: 'Infrastructure, agriculture' },
                  { name: 'Liquid Bulk', growth: '+3.8%', drivers: 'Energy transition, chemicals' },
                  { name: 'Break Bulk', growth: '+6.2%', drivers: 'Project cargo, construction' }
                ]
              }, 'Commodity Forecast')}
            />
          </div>
        </div>
      </div>
      
      <div className="charts-section">
        <div className="chart-container">
          <h3 className="section-title">
            <i className="fas fa-chart-trend"></i>
            <span>Monthly Throughput Forecast for 2024</span>
          </h3>
          <div style={{height: '400px'}}>
            <MonthlyForecastChart 
              onClick={() => openDrillDown && openDrillDown('monthly-forecast', {
                title: 'Monthly Forecast 2024',
                peakMonth: 'October (225,000 T)',
                lowMonth: 'February (185,000 T)',
                average: '204,000 T monthly',
                seasonality: 'Q4 strongest (+12% vs average)'
              }, 'Monthly Forecast')}
            />
          </div>
        </div>
        
        <div className="chart-container">
          <h3 className="section-title">
            <i className="fas fa-tachometer"></i>
            <span>Capacity Utilization Forecast</span>
          </h3>
          <div style={{height: '400px'}}>
            <CapacityForecastChart 
              onClick={() => openDrillDown && openDrillDown('capacity-utilization', {
                title: 'Capacity Utilization Forecast',
                current: '82%',
                q3_2024: '95%',
                criticalPoint: '90% (optimal max)',
                timeToCritical: '6 months',
                actionRequired: 'Immediate expansion planning'
              }, 'Capacity Forecast')}
            />
          </div>
          <div style={{marginTop: '15px',fontSize: '14px', color: '#666'}}>
            <p><i className="fas fa-info-circle" style={{color: '#ff9f40', marginRight: '8px'}}></i> 
            <strong>Note:</strong> Projection shows port reaching 95% capacity by Q3 2024. Expansion planning recommended.</p>
          </div>
        </div>

        <div className="table-container">
          <h3 className="section-title">
            <span>Quarterly Forecast Breakdown</span>
            <i className="fas fa-calendar-alt"></i>
          </h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Quarter</th>
                <th>Forecast</th>
                <th>Confidence</th>
                <th>Growth</th>
              </tr>
            </thead>
            <tbody>
              {forecasts.map((forecast, index) => (
                <tr 
                  key={index}
                  className="clickable-row"
                  onClick={() => handleForecastClick(forecast)}
                >
                  <td><strong>{forecast.quarter}</strong></td>
                  <td>{forecast.forecast}</td>
                  <td><span className={getConfidenceClass(forecast.confidence)}>{forecast.confidence}</span></td>
                  <td><span className="status-indicator status-high">{forecast.growth}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ForecastingTab;